import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CheckCircle, CreditCard, AlertTriangle, Wallet, Users } from 'lucide-react';
import {
  reportsApi,
  CollectionDebtReportDto,
  ReportFilterDto,
  formatCurrency,
  formatDate,
  getPaymentStatusName,
  getPaymentStatusColor,
  handleApiError
} from '../../services/reportsService';

const ReportContainer = styled.div`
  padding: 0;
`;

const StatisticsCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ $accentColor?: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor || 'var(--primary-color)'};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: var(--border-color);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const StatIcon = styled.div<{ $color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$color ? `${props.$color}15` : 'var(--primary-light)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color || 'var(--primary-color)'};
  filter: saturate(0.7) brightness(0.9);
`;

const StatTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const StatValue = styled.div<{ $size?: string }>`
  font-size: ${props => props.$size || '28px'};
  font-weight: 700;
  color: var(--text-primary);

  @media (max-width: 767px) {
    font-size: ${props => props.$size === '24px' ? '20px' : '22px'};
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  padding: 24px;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const TableIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const TableTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
`;

const Th = styled.th`
  background: var(--background-color);
  padding: 16px 20px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
`;

const Td = styled.td`
  padding: 16px 20px;
  text-align: center;
  font-size: 14px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--background-color);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span<{ $bgColor: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => `${props.$bgColor}15`};
  color: ${props => props.$bgColor};
  filter: saturate(0.7) brightness(0.85);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: var(--text-secondary);
  font-size: 15px;
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  color: var(--error-color);
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  font-size: 14px;
`;

interface CollectionDebtReportProps {
  filter: ReportFilterDto;
  // כשמוסתר (למזכירה) — מציגים רק את טבלת הלקוחות ללא כרטיסי הסיכום
  hideSummaryCards?: boolean;
}

const CollectionDebtReport: React.FC<CollectionDebtReportProps> = ({ filter, hideSummaryCards = false }) => {
  const [report, setReport] = useState<CollectionDebtReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsApi.getCollectionDebtReport(filter);
      setReport(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingContainer>טוען דוח גביה וחובות...</LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (!report) {
    return null;
  }

  const stats = report.paymentStatistics;

  return (
    <ReportContainer>
      {!hideSummaryCards && (
      <StatisticsCards>
        <StatCard $accentColor="var(--success-color)">
          <StatHeader>
            <StatIcon $color="var(--success-color)">
              <CheckCircle size={20} />
            </StatIcon>
            <StatTitle>שילמו מראש (במלואו)</StatTitle>
          </StatHeader>
          <StatValue>{stats.customersPaidInFull}</StatValue>
        </StatCard>

        <StatCard $accentColor="var(--warning-color)">
          <StatHeader>
            <StatIcon $color="var(--warning-color)">
              <CreditCard size={20} />
            </StatIcon>
            <StatTitle>בתשלומים חודשיים</StatTitle>
          </StatHeader>
          <StatValue>{stats.customersInInstallments}</StatValue>
        </StatCard>

        <StatCard $accentColor="var(--error-color)">
          <StatHeader>
            <StatIcon $color="var(--error-color)">
              <AlertTriangle size={20} />
            </StatIcon>
            <StatTitle>עם חוב</StatTitle>
          </StatHeader>
          <StatValue>{stats.customersWithDebt}</StatValue>
        </StatCard>

        <StatCard $accentColor="var(--error-color)">
          <StatHeader>
            <StatIcon $color="var(--error-color)">
              <Wallet size={20} />
            </StatIcon>
            <StatTitle>סה"כ חובות פתוחים</StatTitle>
          </StatHeader>
          <StatValue $size="24px">{formatCurrency(stats.totalOpenDebts)}</StatValue>
        </StatCard>
      </StatisticsCards>
      )}

      <TableContainer>
        <TableHeader>
          <TableIcon>
            <Users size={18} />
          </TableIcon>
          <TableTitle>פירוט לפי לקוח</TableTitle>
        </TableHeader>
        <Table>
          <thead>
            <tr>
              <Th>שם לקוח</Th>
              <Th>סטטוס תשלום</Th>
              <Th>סה"כ חיוב (כולל מע"מ)</Th>
              <Th>שולם עד כה</Th>
              <Th>יתרת חוב</Th>
              <Th>תאריך תשלום אחרון</Th>
            </tr>
          </thead>
          <tbody>
            {report.customerDetails.map((customer) => (
              <Tr key={customer.clientId}>
                <Td style={{ fontWeight: 600 }}>
                  {customer.customerName}
                </Td>
                <Td>
                  <StatusBadge $bgColor={getPaymentStatusColor(customer.paymentStatus)}>
                    {getPaymentStatusName(customer.paymentStatus)}
                  </StatusBadge>
                </Td>
                <Td>{formatCurrency(customer.totalChargeWithVat)}</Td>
                <Td style={{ color: '#16a34a', fontWeight: 600, filter: 'saturate(0.7) brightness(0.85)' }}>
                  {formatCurrency(customer.totalPaid)}
                </Td>
                <Td style={{
                  color: customer.balance > 0 ? '#dc2626' : '#16a34a',
                  fontWeight: 600,
                  filter: 'saturate(0.7) brightness(0.85)'
                }}>
                  {formatCurrency(customer.balance)}
                </Td>
                <Td>{formatDate(customer.lastPaymentDate)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </ReportContainer>
  );
};

export default CollectionDebtReport;
